/**
 * The scenario-run model — PD-5.
 *
 * Pure, for the same reason `matchevents/model.ts` is: what this half of the
 * package is judged on ("a scenario run result is viewable from admin", and the
 * legacy-vs-split comparison beside it) is which runs pair up, which sides can
 * be compared, and whether a run passed — none of which needs a DOM.
 *
 * ## A run row is a receipt, not an authority
 *
 * `test_scenario_runs` (migration 033) stores what the harness reported: the
 * path, the outcome, the identifiers each side produced, and the pairwise
 * grade. The prose and the transcript stay in the markdown run document. Every
 * id in a run points into a store that holds the real thing, so the screens
 * re-read stories, matches and parity rows live rather than trusting the
 * numbers pasted here — and `gradeVerdict` recomputes `pass` from
 * expected-vs-actual whenever the poster left it out, so a partially filled run
 * still grades.
 */
import type {
  ScenarioRun,
  ScenarioRunGrade,
  ScenarioRunPath,
  ScenarioRunSide,
  TestScenario,
} from '../../api/types';

export const RUN_PATHS: readonly ScenarioRunPath[] = ['legacy', 'split'];

/**
 * Did this pair come out the way the scenario said it should?
 *
 *  true / false  the oracle was stated and the run answered it
 *  null          ungraded — the run recorded no actual, or the scenario stated
 *                no expectation. NOT `false`: "we do not know" and "it failed"
 *                are the two things an operator must never see conflated.
 */
export function gradeVerdict(grade: ScenarioRunGrade): boolean | null {
  if (typeof grade.pass === 'boolean') return grade.pass;
  if (typeof grade.expectedMatch !== 'boolean' || typeof grade.actualMatch !== 'boolean') {
    return null;
  }
  return grade.expectedMatch === grade.actualMatch;
}

export interface RunTally {
  passed: number;
  failed: number;
  ungraded: number;
  total: number;
}

export function tallyGrades(run: ScenarioRun): RunTally {
  const grades = run.grades ?? [];
  let passed = 0;
  let failed = 0;
  let ungraded = 0;
  for (const g of grades) {
    const v = gradeVerdict(g);
    if (v === true) passed += 1;
    else if (v === false) failed += 1;
    else ungraded += 1;
  }
  return { passed, failed, ungraded, total: grades.length };
}

/**
 * What to draw on the run's badge.
 *
 * The stored `status` wins whenever it is a statement about the RUN rather than
 * about the grades — `running` and `error` are conditions of the harness, and a
 * crashed run's grades are not evidence of anything. Otherwise the grades
 * decide, because a run stored as `passed` whose grades disagree is a stale
 * receipt, and the screen should say what the grades say.
 */
export type RunVerdict = 'running' | 'error' | 'passed' | 'failed' | 'ungraded';

export function runVerdict(run: ScenarioRun): RunVerdict {
  if (run.status === 'running' || run.status === 'error') return run.status;
  const tally = tallyGrades(run);
  if (tally.total === 0) return run.status === 'failed' ? 'failed' : 'ungraded';
  if (tally.failed > 0) return 'failed';
  if (tally.passed > 0) return 'passed';
  return 'ungraded';
}

/** Sides with a pebble id — what a run's parity report can be scoped to. */
export function runPebbleIds(run: ScenarioRun | null | undefined): string[] {
  const seen = new Set<string>();
  for (const side of run?.sides ?? []) {
    if (side.pebbleId) seen.add(side.pebbleId);
  }
  return [...seen];
}

export function sidesByLabel(run: ScenarioRun): Map<string, ScenarioRunSide> {
  const out = new Map<string, ScenarioRunSide>();
  for (const side of run.sides ?? []) {
    if (side.label && !out.has(side.label)) out.set(side.label, side);
  }
  return out;
}

/** The newest run on each path — what the comparison defaults to. */
export function latestByPath(runs: readonly ScenarioRun[]): Record<ScenarioRunPath, ScenarioRun | null> {
  const out: Record<ScenarioRunPath, ScenarioRun | null> = { legacy: null, split: null };
  for (const run of [...runs].sort(byStartedDesc)) {
    if (run.path === 'legacy' && !out.legacy) out.legacy = run;
    if (run.path === 'split' && !out.split) out.split = run;
  }
  return out;
}

export function byStartedDesc(a: ScenarioRun, b: ScenarioRun): number {
  return Date.parse(b.startedAt ?? '') - Date.parse(a.startedAt ?? '') || 0;
}

export interface ComparableSide {
  label: string;
  legacy: ScenarioRunSide;
  split: ScenarioRunSide;
}

/**
 * The sides a legacy run and a split run can actually be put next to each
 * other: same label, a story id on both. A side that submitted on one path and
 * not the other is NOT comparable — that is a finding, and it is reported as a
 * gap (`incomparable`) rather than drawn as half a comparison.
 */
export function comparableSides(
  legacy: ScenarioRun | null,
  split: ScenarioRun | null,
): { pairs: ComparableSide[]; incomparable: string[] } {
  if (!legacy || !split) return { pairs: [], incomparable: [] };
  const l = sidesByLabel(legacy);
  const s = sidesByLabel(split);
  const labels = [...new Set([...l.keys(), ...s.keys()])];
  const pairs: ComparableSide[] = [];
  const incomparable: string[] = [];
  for (const label of labels) {
    const a = l.get(label);
    const b = s.get(label);
    if (a?.storyId && b?.storyId) pairs.push({ label, legacy: a, split: b });
    else incomparable.push(label);
  }
  return { pairs, incomparable };
}

/**
 * The cheap half of §5.5's "compare submitted `story_text` coverage": how much
 * text each path actually submitted for the same side. It is not a quality
 * judgement — a shorter submit can be a better one — but a path that submits
 * half the words is a finding worth seeing before reading two stories in full.
 */
export interface TextStats {
  chars: number;
  words: number;
}

export function textStats(text: string | null | undefined): TextStats {
  const t = (text ?? '').trim();
  return { chars: t.length, words: t ? t.split(/\s+/).length : 0 };
}

/** A run's own duration, when it recorded both ends. */
export function runDurationMs(run: ScenarioRun): number | null {
  const start = Date.parse(run.startedAt ?? '');
  const end = Date.parse(run.finishedAt ?? '');
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return end - start;
}

// ---------------------------------------------------------------------------
// Launching
// ---------------------------------------------------------------------------

/**
 * The commands that run this scenario, for copying.
 *
 * The admin cannot launch a run itself and does not pretend to: a run drives
 * the local stack, the real chat models and the local matcher from the
 * operator's machine (`.claude/skills/run-test-scenario`), all of which live
 * far outside a Worker's reach. What the admin can do — and what makes it the
 * "replay harness front end" §11 asks for — is hand over the exact invocation
 * for this scenario on either path, and then be the place the result is read.
 *
 * Only env-var NAMES appear. A command with a token in it is a command that
 * ends up in a screenshot.
 */
export function launchCommands(scenario: TestScenario, path: ScenarioRunPath): string {
  const flag = path === 'split' ? ' --path=split' : '';
  return [
    '# 1 · bring the local stack up (Postgres, api stub, mastra, matcher)',
    'cd apps/mastra && local/up.sh',
    '',
    '# 2 · point the harness at this catalog (never prints secrets)',
    'source test-env/harness.env.sh --local',
    '',
    '# 3 · in Claude Code, run the scenario on this path',
    `/run-test-scenario ${scenario.name}${flag}`,
  ].join('\n');
}

/**
 * The curl that files the receipt this screen reads. Written out in full
 * because the harness posts it by hand today (see the decision doc): the run
 * skill's own recording step is a follow-up in the mastra lane.
 */
export function recordRunCommand(scenario: TestScenario, path: ScenarioRunPath): string {
  const body = {
    path,
    status: 'passed',
    stack: 'local',
    startedAt: '<ISO>',
    finishedAt: '<ISO>',
    sides: (scenario.sides ?? []).map((s) => ({
      label: s.label,
      storyId: '<story id>',
      pebbleId: '<pebble id>',
      storyVersion: 1,
      requestKey: '<pebbleId>_1',
    })),
    grades: (scenario.expectedMatches ?? []).map((m) => ({
      a: m.a,
      b: m.b,
      expectedMatch: m.match,
      actualMatch: true,
      matchId: '<match id>',
    })),
    docPath: 'apps/mastra/test-runs/<file>.md',
  };
  return [
    `curl -sS -X POST "$ADMIN_API_URL/admin/test/scenarios/${scenario.id}/runs" \\`,
    '  -H "Authorization: Bearer $ADMIN_TOKEN" \\',
    '  -H "content-type: application/json" \\',
    `  -d '${JSON.stringify(body)}'`,
  ].join('\n');
}
